import * as THREE from "three";
import { DeviceOrientationControls } from "three/examples/jsm/controls/DeviceOrientationControls";
import noop from "lodash.noop";

import AbstractARExperience from "./AbstractARExperience";
import PlacegroundPipeline from "./PlacegroundPipeline";
import { VideoComponent } from "@/argo_ar/argo_webar_lib/components/videoComponent";

class GyroARExperience extends AbstractARExperience {
    private scene: THREE.Scene;
    private camera: THREE.PerspectiveCamera;
    private renderer: THREE.WebGLRenderer;
    private videoContainer: HTMLElement;
    private videoComponent: VideoComponent;
    private deviceOrientationControls: DeviceOrientationControls;

    constructor(
        canvas: HTMLCanvasElement,
        pageId: number,
        deviceId: string,
        onAssetPlayStartListener: Function = noop,
        onAssetPlayStopListener: Function = noop,
        onVideoPlayStartListener: Function = noop,
        onAssetLoadStartListener: Function = noop,
        onAssetLoadFinishedListener: Function = noop,
        onAssetDisplayedListener: Function = noop,
        onAssetHiddenListener: Function = noop,
        onErrorListener: Function = noop,
        videoContainer: HTMLElement
    ) {
        super(
            canvas,
            pageId,
            deviceId,
            onAssetPlayStartListener,
            onAssetPlayStopListener,
            onVideoPlayStartListener,
            onAssetLoadStartListener,
            onAssetLoadFinishedListener,
            onAssetDisplayedListener,
            onAssetHiddenListener,
            onErrorListener
        );

        this.videoContainer = videoContainer;
    }

    animate = () => {
        requestAnimationFrame(this.animate);
        this.renderer.render(this.scene, this.camera);
    };

    public initialize() {
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(
            60,
            window.innerWidth / window.innerHeight,
            0.01,
            1000
        );
        this.camera.position.set(0, 3, 0);
        this.scene.add(this.camera);

        this.renderer = new THREE.WebGLRenderer({
            alpha: true,
            canvas: this.canvas,
            antialias: true
        });
        this.renderer.outputEncoding = THREE.sRGBEncoding;
        this.renderer.setSize(window.innerWidth, window.innerHeight);

        this.placegroundPipeline = new PlacegroundPipeline(
            this.pageId,
            this.deviceId,
            this.listeners,
            7
        );

        this.videoComponent = new VideoComponent(this.videoContainer);
    }

    startPlacegroundSession() {
        return new Promise(resolve => {
            this.animate();

            if (this.deviceOrientationControls) {
                this.deviceOrientationControls.disconnect();
                this.deviceOrientationControls.connect();
            } else {
                this.deviceOrientationControls = new DeviceOrientationControls(this.camera);
            }

            setTimeout(() => {
                const orientation = this.deviceOrientationControls.deviceOrientation;
                if (
                    orientation === {} ||
                    typeof orientation.alpha === "undefined" ||
                    orientation.alpha === null
                ) {
                    this.onErrorListener({
                        message: "errors.noGyro"
                    });
                } else {
                    console.log("Gyro OK");
                }
            }, 2000);

            this.videoComponent.start();
            this.placegroundPipeline.startWithGyro(
                this.canvas,
                this.scene,
                this.camera,
                this.renderer
            );

            window.addEventListener(
                "deviceorientation",
                this.onDeviceOrientationChangeEvent,
                false
            );
            resolve(true);
        });
    }

    stopPlacegroundSession() {
        this.videoComponent.stop();
        this.deviceOrientationControls.disconnect();
        this.placegroundPipeline.stopPlayingAsset();

        window.removeEventListener("deviceorientation", this.onDeviceOrientationChangeEvent);
    }

    onDeviceOrientationChangeEvent = () => {
        this.deviceOrientationControls.update();
    };
}

export default GyroARExperience;
